import type { State } from '../types';
import { uid, ensureLocalStructures } from './dataMapping';
import { toNumber } from './formatting';
import { save } from './state';
import { submitRemoteLot } from './api-operations';
import { mapRemoteDocument } from './lotOperations';
import { ensureAccessToken } from './api';

export interface LotFormFields {
  id?: HTMLInputElement;
  title?: HTMLInputElement;
  source?: HTMLSelectElement;
  nextRequiredBid?: HTMLInputElement;
  close?: HTMLInputElement;
  url?: HTMLInputElement;
  description?: HTMLTextAreaElement;
}

export interface LotDraft {
  id: string;
  title: string;
  source: string;
  currentBid?: number;
  closeAt?: number;
  url?: string;
  notes?: string;
  createdAt?: number;
  updatedAt: number;
  favorite: boolean;
  remote: boolean;
}

export function buildLotDraft(fields: LotFormFields, existingLot: any, now: number): LotDraft {
  const id = fields.id?.value || uid();
  const favorite = existingLot ? !!(existingLot as { favorite?: boolean }).favorite : false;

  const currentBid = fields.nextRequiredBid?.value
    ? toNumber(fields.nextRequiredBid.value)
    : undefined;

  const closeAt = fields.close?.value ? new Date(fields.close.value).getTime() : undefined;
  const url = fields.url?.value?.trim() || undefined;
  const title = fields.title?.value?.trim() || '';

  const lot: LotDraft = ensureLocalStructures({
    id,
    title,
    source: fields.source?.value || '',
    currentBid,
    closeAt: Number.isFinite(closeAt || NaN) ? (closeAt as number) : undefined,
    url,
    notes: fields.description?.value?.trim() || undefined,
    createdAt: existingLot ? ((existingLot as { createdAt?: number }).createdAt ?? now) : now,
    updatedAt: now,
    favorite,
    remote: false,
  }) as LotDraft;

  return lot;
}

export interface LotSubmissionDeps {
  state: State;
  render: () => void;
  applyModeBanner: () => void;
  els: { form?: HTMLFormElement; f?: LotFormFields };
  DEBUG: boolean;
}

export async function submitLotDraft(
  draft: LotDraft,
  deps: LotSubmissionDeps
): Promise<{ success: boolean; mode: 'remote' | 'local' }> {
  const { state, render, applyModeBanner, els, DEBUG } = deps;

  if (state.mode === 'auth0') {
    const success = await submitRemoteLot(
      draft,
      false,
      state,
      ensureAccessToken,
      mapRemoteDocument,
      render,
      applyModeBanner,
      els,
      DEBUG
    );

    return { success, mode: 'remote' };
  }

  const idx = state.lots.findIndex((x: unknown) => (x as { id: string }).id === draft.id);

  if (idx >= 0) {
    const merged = Object.assign({}, state.lots[idx], draft, {
      id: draft.id,
      createdAt: (state.lots[idx] as Record<string, unknown>).createdAt ?? draft.createdAt,
      updatedAt: draft.updatedAt,
    });
    state.lots[idx] = ensureLocalStructures(merged) as any;
  } else {
    const created = Object.assign({}, draft, { createdAt: draft.createdAt ?? draft.updatedAt });
    state.lots.unshift(ensureLocalStructures(created) as any);
  }

  save();
  render();

  return { success: true, mode: 'local' };
}
